import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
  ErrorSnackBarData,
  NormalSnackBarData,
  SnackBarService,
} from '@qtek/libs/snack-bar-core';
import { extractRefId, isNil } from '@qtek/shared/utils';
import {
  catchError,
  EMPTY,
  filter,
  from,
  groupBy,
  map,
  merge,
  mergeMap,
  of,
  share,
  switchMap,
  takeUntil,
} from 'rxjs';
import { TasksActions } from '../tasks';
import { TriggerService } from './trigger.service';
import { WorkflowStateService } from './workflow-state.service';
import { WorkflowTaskConfigurationService } from './workflow-task-configuration.service';
import { WorkflowActions } from './workflow.actions';
import { WorkflowService } from './workflow.service';

@Injectable()
export class WorkflowEffects {
  private readonly actions$ = inject(Actions);
  private readonly workflowService = inject(WorkflowService);
  private readonly triggerService = inject(TriggerService);
  private readonly workflowStateService = inject(WorkflowStateService);
  private readonly workflowTaskConfigService = inject(
    WorkflowTaskConfigurationService
  );
  private readonly snackBarService = inject(SnackBarService);

  createWorkflow$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(WorkflowActions.createWorkflow),
        map(({ payload }) => {
          this.workflowService.createWorkflow(payload);
        })
      ),
    { dispatch: false }
  );

  createSubWorkflow$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(WorkflowActions.createSubWorkflow),
        map(({ payload }) => this.workflowService.createWorkflow(payload))
      ),
    { dispatch: false }
  );

  createTrigger$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(WorkflowActions.createTrigger),
        map(({ payload }) => {
          this.triggerService.createTrigger(payload);
        })
      ),
    { dispatch: false }
  );

  updateTrigger$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(WorkflowActions.updateTrigger),
        map(({ payload, id }) => {
          this.triggerService.updateTrigger(payload, id);
        })
      ),
    { dispatch: false }
  );

  deleteTrigger$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(WorkflowActions.deleteTrigger),
        map(({ id, refId }) => {
          this.triggerService.deleteTrigger(id, refId);
        })
      ),
    { dispatch: false }
  );

  createWorkflowAction$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(WorkflowActions.createWorkflowAction),
        map(({ payload }) => {
          this.workflowTaskConfigService.createTask(payload);
        })
      ),
    { dispatch: false }
  );

  updateWorkflowAction$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(WorkflowActions.updateWorkflowAction),
        map(({ payload, taskId }) => {
          this.workflowTaskConfigService.updateTask(payload, taskId);
        })
      ),
    { dispatch: false }
  );

  removeWorkflowAction$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(WorkflowActions.removeWorkflowAction),
        map(({ taskId, workflowId }) => {
          this.workflowTaskConfigService.deleteTask(taskId, workflowId);
        })
      ),
    { dispatch: false }
  );

  updateWorkflow$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(WorkflowActions.updateWorkflow),
        map(({ payload, id }) => {
          this.workflowService.updateWorkflow(payload, id);
        })
      ),
    { dispatch: false }
  );

  deleteWorkflow$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(WorkflowActions.deleteWorkflow),
        map(({ id, refId }) => {
          this.workflowService.deleteWorkflow(id, refId);
        })
      ),
    { dispatch: false }
  );

  swapActions$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(WorkflowActions.swapActions),
        map(({ draggedStepId, targetStepId }) => {
          this.workflowTaskConfigService.swapTasks(draggedStepId, targetStepId);
        })
      ),
    { dispatch: false }
  );

  swapActionsSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WorkflowActions.swapActions),
      mergeMap(({ refId }) => {
        return this.actions$.pipe(
          ofType(TasksActions.updateTaskSuccess),
          filter(() => true),
          mergeMap(() => {
            return [WorkflowActions.swapActionsSuccess({ refId })];
          })
        );
      })
    )
  );

  triggerWorkflow$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WorkflowActions.triggerWorkflow),
      mergeMap(({ triggerId, refId }) =>
        this.workflowService.triggerWorkflow(triggerId, refId).pipe(
          map(response => {
            if (response?.res) {
              const snackBarData: NormalSnackBarData = {
                type: 'NormalMessage',
                message: 'GNR_WORKFLOW_TRIGGERED_SUCCESSFULLY',
              };
              this.snackBarService.showSnackBar(snackBarData);
              return WorkflowActions.triggerWorkflowSuccess({
                workflow: response.res,
                refId,
              });
            } else {
              const snackBarData: ErrorSnackBarData = {
                type: 'ErrorMessage',
                message: 'GNR_WORKFLOW_TRIGGER_FAILED',
              };
              this.snackBarService.showSnackBar(snackBarData);
              return WorkflowActions.triggerWorkflowFailure({
                error: new Error('GNR_WORKFLOW_TRIGGER_FAILED'),
                refId,
              });
            }
          }),
          catchError(error => {
            const snackBarData: ErrorSnackBarData = {
              type: 'ErrorMessage',
              message: 'GNR_WORKFLOW_TRIGGER_FAILED',
            };
            this.snackBarService.showSnackBar(snackBarData);
            return of(
              WorkflowActions.triggerWorkflowFailure({
                error,
                refId,
              })
            );
          })
        )
      )
    )
  );

  loadRoleParticipants$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        WorkflowActions.loadRoleParticipants,
        WorkflowActions.clearRoleParticipants
      ),
      groupBy(action => action.refId),
      mergeMap(group$ =>
        group$.pipe(
          switchMap(action => {
            if (action.type === WorkflowActions.clearRoleParticipants.type) {
              return EMPTY;
            }
            const { refId, roleIds } = action;
            return this.workflowService
              .getParticipantsByRoles(extractRefId(refId), roleIds)
              .pipe(
                map(response => {
                  if (response?.res) {
                    return WorkflowActions.loadRoleParticipantsSuccess({
                      refId,
                      participants: response.res,
                    });
                  } else {
                    return WorkflowActions.loadRoleParticipantsFailure({
                      refId,
                      error: new Error('Failed to load role participants'),
                    });
                  }
                }),
                catchError(error =>
                  of(
                    WorkflowActions.loadRoleParticipantsFailure({
                      refId,
                      error,
                    })
                  )
                )
              );
          })
        )
      )
    )
  );

  getEmailDynamicContent$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WorkflowActions.getEmailDynamicContent),
      mergeMap(({ taskId }) =>
        this.workflowService.getEmailDynamicContent(taskId).pipe(
          map(response => {
            if (response?.res) {
              return WorkflowActions.getEmailDynamicContentSuccess({
                dynamicContent: response.res,
              });
            } else {
              return WorkflowActions.getEmailDynamicContentFailure({
                error: new Error('Failed to get email dynamic content'),
              });
            }
          }),
          catchError(error => {
            return of(
              WorkflowActions.getEmailDynamicContentFailure({
                error,
              })
            );
          })
        )
      )
    )
  );

  getEmailTemplates$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WorkflowActions.getEmailTemplates),
      mergeMap(() =>
        this.workflowService.getEmailTemplates().pipe(
          map(response => {
            if (response?.res) {
              return WorkflowActions.getEmailTemplatesSuccess({
                templates: response.res,
              });
            } else {
              return WorkflowActions.getEmailTemplatesFailure({
                error: new Error('Failed to get email templates'),
              });
            }
          }),
          catchError(error => {
            return of(
              WorkflowActions.getEmailTemplatesFailure({
                error,
              })
            );
          })
        )
      )
    )
  );

  getWorkflow$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(WorkflowActions.getWorkflow),
        map(({ id, refId }) => {
          if (id !== refId) {
            this.workflowService.getWorkflowDetail(id);
          } else {
            this.workflowService.getWorkflow(id);
          }
        })
      ),
    { dispatch: false }
  );

  unsubscribeWorkflows$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(WorkflowActions.unsubscribeWorkflows),
        map(({ refId }) => {
          console.log('[Workflow Effects] Unsubscribed from workflows:', refId);
        })
      ),
    { dispatch: false }
  );

  subscribeWorkflowDetail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WorkflowActions.subscribeWorkflowDetail),
      mergeMap(action => {
        const { workflowId, refId } = action;
        return this.workflowService.subscribeWorkflowDetail(workflowId).pipe(
          map(message => ({
            ...message,
            subscriptionRefId: refId,
            subscriptionWorkflowId: workflowId,
          })),
          takeUntil(
            this.actions$.pipe(
              ofType(WorkflowActions.unsubscribeWorkflowDetail),
              filter(a => a.workflowId === workflowId)
            )
          )
        );
      }),
      filter((data: any) => data.op),
      mergeMap(
        ({ op, res, sts, subscriptionRefId, subscriptionWorkflowId }: any) => {
          switch (op) {
            case 'query':
              if (res && res.length > 0) {
                return of(
                  WorkflowActions.getWorkflowSuccess({
                    workflow: res[0],
                    refId: subscriptionRefId,
                  })
                );
              }
              return of({ type: 'NOT_EXIST' } as any);

            case 'ins':
            case 'upd': {
              const workflow = Array.isArray(res) ? res[0] : res;
              if (workflow && workflow._id === subscriptionWorkflowId) {
                return of(
                  WorkflowActions.getWorkflowSuccess({
                    workflow,
                    refId: subscriptionRefId,
                  })
                );
              }
              return of({ type: 'NOT_EXIST' } as any);
            }

            default:
              return of({ type: 'NOT_EXIST' } as any);
          }
        }
      ),
      catchError(error =>
        of(WorkflowActions.getWorkflowFailure({ error, refId: '' }))
      )
    )
  );

  unsubscribeWorkflowDetail$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(WorkflowActions.unsubscribeWorkflowDetail),
        map(({ workflowId }) => {
          console.log(
            '[Workflow Effects] Unsubscribed from workflow detail:',
            workflowId
          );
        })
      ),
    { dispatch: false }
  );

  subscribeWorkflows$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WorkflowActions.subscribeWorkflows),
      mergeMap(action => {
        const { payload, refId } = action;
        return this.workflowService.subscribe(payload, refId).pipe(
          map(message => ({ ...message, subscriptionRefId: refId })),
          takeUntil(
            this.actions$.pipe(
              ofType(WorkflowActions.unsubscribeWorkflows),
              filter(unsubscribeAction => {
                return unsubscribeAction.refId === refId;
              })
            )
          )
        );
      }),
      filter((data: any) => data.op),
      mergeMap(({ op, res, sts, subscriptionRefId }: any) => {
        switch (op) {
          case 'query':
            if (res || !isNil(sts?.meta)) {
              // Skip the cross-deal "myDeals" query response — it shares the
              // workflow entity channel but its workflows have foreign refIds.
              if (
                Array.isArray(res) &&
                res.length > 0 &&
                res.some((w: any) => w?.refId && w.refId !== subscriptionRefId)
              ) {
                return EMPTY;
              }
              const actions: any[] = [
                WorkflowActions.loadWorkflowsSuccess({
                  workflows: res || [],
                  refId: subscriptionRefId,
                }),
              ];

              return from(actions);
            } else {
              return of(
                WorkflowActions.loadWorkflowsFailure({
                  error: new Error(
                    sts?.message || 'GNR_FAILED_TO_SUBSCRIBE_WORKFLOWS'
                  ),
                  refId: subscriptionRefId,
                })
              );
            }

          case 'get':
            if (res && !res.parentWorkflowId) {
              return of(
                WorkflowActions.getWorkflowSuccess({
                  workflow: res,
                  refId: subscriptionRefId,
                })
              );
            } else {
              return of(
                WorkflowActions.getWorkflowFailure({
                  error: new Error(
                    sts?.message || 'GNR_FAILED_TO_GET_WORKFLOW'
                  ),
                  refId: subscriptionRefId,
                })
              );
            }

          case 'ins':
            if (res) {
              if (res.parentWorkflowId) {
                return of(
                  WorkflowActions.createSubWorkflowSuccess({
                    subWorkflow: res,
                    refId: subscriptionRefId,
                    mysid: subscriptionRefId,
                  })
                );
              }

              const snackBarData: NormalSnackBarData = {
                type: 'NormalMessage',
                message: 'GNR_WORKFLOW_CREATED_SUCCESSFULLY',
              };
              this.snackBarService.showSnackBar(snackBarData);

              return of(
                WorkflowActions.createWorkflowSuccess({
                  workflow: res,
                  refId: subscriptionRefId,
                })
              );
            } else {
              const snackBarData: ErrorSnackBarData = {
                type: 'ErrorMessage',
                message: 'GNR_WORKFLOW_CREATE_FAILED',
              };
              this.snackBarService.showSnackBar(snackBarData);
              return of(
                WorkflowActions.createWorkflowFailure({
                  error: new Error(
                    sts?.message || 'GNR_WORKFLOW_CREATE_FAILED'
                  ),
                  refId: subscriptionRefId,
                })
              );
            }

          case 'upd':
            if (res && !res.parentWorkflowId) {
              const snackBarData: NormalSnackBarData = {
                type: 'NormalMessage',
                message: 'GNR_WORKFLOW_UPDATED_SUCCESSFULLY',
              };
              this.snackBarService.showSnackBar(snackBarData);
              return of(
                WorkflowActions.updateWorkflowSuccess({
                  workflow: res,
                  refId: subscriptionRefId,
                })
              );
            } else {
              const snackBarData: ErrorSnackBarData = {
                type: 'ErrorMessage',
                message: 'GNR_WORKFLOW_UPDATE_FAILED',
              };
              this.snackBarService.showSnackBar(snackBarData);
              return of(
                WorkflowActions.updateWorkflowFailure({
                  error: new Error(
                    sts?.message || 'GNR_WORKFLOW_UPDATE_FAILED'
                  ),
                })
              );
            }

          case 'del':
            if (res) {
              const snackBarData: NormalSnackBarData = {
                type: 'NormalMessage',
                message: 'GNR_WORKFLOW_DELETED_SUCCESSFULLY',
              };
              this.snackBarService.showSnackBar(snackBarData);
              return of(
                WorkflowActions.deleteWorkflowSuccess({
                  id: res._id,
                  refId: res.refId,
                })
              );
            } else {
              const snackBarData: ErrorSnackBarData = {
                type: 'ErrorMessage',
                message: 'GNR_WORKFLOW_DELETE_FAILED',
              };
              this.snackBarService.showSnackBar(snackBarData);
              return of(
                WorkflowActions.deleteWorkflowFailure({
                  error: new Error(
                    sts?.message || 'GNR_WORKFLOW_DELETE_FAILED'
                  ),
                })
              );
            }

          default:
            return of({ type: 'NOT_EXIST' });
        }
      }),
      catchError(error =>
        of(WorkflowActions.loadWorkflowsFailure({ error, refId: '' }))
      )
    )
  );

  unsubscribeTriggers$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(WorkflowActions.unsubscribeTriggers),
        map(({ refId }) => {
          console.log('[Workflow Effects] Unsubscribed from triggers:', refId);
        })
      ),
    { dispatch: false }
  );

  subscribeTriggers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WorkflowActions.subscribeTriggers),
      mergeMap(action => {
        const { refId } = action;
        return this.triggerService.subscribe().pipe(
          map(message => ({ ...message, subscriptionRefId: refId })),
          takeUntil(
            this.actions$.pipe(
              ofType(WorkflowActions.unsubscribeTriggers),
              filter(unsubscribeAction => {
                return unsubscribeAction.refId === refId;
              })
            )
          )
        );
      }),
      filter((data: any) => data.op),
      mergeMap(({ op, res, sts, subscriptionRefId }: any) => {
        switch (op) {
          case 'ins':
            if (res) {
              const snackBarData: NormalSnackBarData = {
                type: 'NormalMessage',
                message: 'GNR_TRIGGER_CREATED_SUCCESSFULLY',
              };
              this.snackBarService.showSnackBar(snackBarData);
              return of(
                WorkflowActions.createTriggerSuccess({
                  trigger: res,
                  refId: subscriptionRefId,
                })
              );
            } else {
              const snackBarData: ErrorSnackBarData = {
                type: 'ErrorMessage',
                message: 'GNR_TRIGGER_CREATE_FAILED',
              };
              this.snackBarService.showSnackBar(snackBarData);
              return of(
                WorkflowActions.createTriggerFailure({
                  error: new Error(sts?.message || 'GNR_TRIGGER_CREATE_FAILED'),
                  refId: subscriptionRefId,
                })
              );
            }

          case 'upd':
            if (res) {
              return of(
                WorkflowActions.updateTriggerSuccess({
                  trigger: res,
                  refId: subscriptionRefId,
                })
              );
            } else {
              return of(
                WorkflowActions.updateTriggerFailure({
                  error: new Error(sts?.message || 'GNR_TRIGGER_UPDATE_FAILED'),
                  refId: subscriptionRefId,
                })
              );
            }

          case 'del':
            if (res) {
              const snackBarData: NormalSnackBarData = {
                type: 'NormalMessage',
                message: 'GNR_TRIGGER_DELETED_SUCCESSFULLY',
              };
              this.snackBarService.showSnackBar(snackBarData);
              return of(
                WorkflowActions.deleteTriggerSuccess({
                  trigger: res,
                  refId: subscriptionRefId,
                })
              );
            } else {
              const snackBarData: ErrorSnackBarData = {
                type: 'ErrorMessage',
                message: 'GNR_TRIGGER_DELETE_FAILED',
              };
              this.snackBarService.showSnackBar(snackBarData);
              return of(
                WorkflowActions.deleteTriggerFailure({
                  error: new Error(sts?.message || 'GNR_TRIGGER_DELETE_FAILED'),
                  refId: subscriptionRefId,
                })
              );
            }

          default:
            return of({ type: 'NOT_EXIST' });
        }
      }),
      catchError(error =>
        of(WorkflowActions.loadTriggersFailure({ error, refId: '' }))
      )
    )
  );

  unsubscribeWorkflowTasks$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(WorkflowActions.unsubscribeWorkflowTasks),
        map(({ mysid, refId }) => {
          console.log(
            '[Workflow Effects] Unsubscribed from workflow tasks:',
            mysid,
            refId
          );
        })
      ),
    { dispatch: false }
  );

  subscribeWorkflowTasks$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WorkflowActions.subscribeWorkflowTasks),
      mergeMap(action => {
        const { payload, mysid, refId } = action;

        return this.workflowTaskConfigService.subscribe(payload).pipe(
          map(message => ({
            ...message,
            subscriptionRefId: refId,
            subscriptionMysid: mysid,
          })),
          takeUntil(
            this.actions$.pipe(
              ofType(WorkflowActions.unsubscribeWorkflowTasks),
              filter(unsubscribeAction => {
                return (
                  unsubscribeAction.mysid === mysid &&
                  unsubscribeAction.refId === refId
                );
              })
            )
          )
        );
      }),
      filter((data: any) => data.op),
      mergeMap(
        ({ op, res, sts, subscriptionRefId, subscriptionMysid }: any) => {
          switch (op) {
            case 'ins':
              if (res) {
                const snackBarData: NormalSnackBarData = {
                  type: 'NormalMessage',
                  message: 'GNR_TASK_CREATED_SUCCESSFULLY',
                };
                this.snackBarService.showSnackBar(snackBarData);

                return of(
                  WorkflowActions.createWorkflowActionSuccess({
                    task: res,
                    workflowId: res.workflowId,
                    refId: subscriptionRefId,
                  })
                );
              } else {
                const snackBarData: ErrorSnackBarData = {
                  type: 'ErrorMessage',
                  message: 'GNR_TASK_CREATE_FAILED',
                };
                this.snackBarService.showSnackBar(snackBarData);
                return of(
                  WorkflowActions.createWorkflowTaskConfigurationFailure({
                    error: new Error(sts?.message || 'GNR_TASK_CREATE_FAILED'),
                    mysid: subscriptionMysid,
                    refId: subscriptionRefId,
                  })
                );
              }

            case 'upd':
            case 'upds':
              if (res) {
                const snackBarData: NormalSnackBarData = {
                  type: 'NormalMessage',
                  message: 'GNR_TASK_UPDATED_SUCCESSFULLY',
                };
                this.snackBarService.showSnackBar(snackBarData);

                return of(
                  WorkflowActions.updateWorkflowActionSuccess({
                    task: res,
                    workflowId: res.workflowId,
                    refId: subscriptionRefId,
                  })
                );
              } else {
                const snackBarData: ErrorSnackBarData = {
                  type: 'ErrorMessage',
                  message: 'GNR_TASK_UPDATE_FAILED',
                };
                this.snackBarService.showSnackBar(snackBarData);
                return of(
                  WorkflowActions.updateWorkflowTaskConfigurationFailure({
                    error: new Error(sts?.message || 'GNR_TASK_UPDATE_FAILED'),
                    mysid: subscriptionMysid,
                    refId: subscriptionRefId,
                  })
                );
              }

            case 'del':
              if (res) {
                const snackBarData: NormalSnackBarData = {
                  type: 'NormalMessage',
                  message: 'GNR_TASK_DELETED_SUCCESSFULLY',
                };
                this.snackBarService.showSnackBar(snackBarData);
                const { _id, workflowId, refId } = res;

                return of(
                  WorkflowActions.removeWorkflowActionSuccess({
                    taskId: _id,
                    workflowId,
                    refId,
                  })
                );
              } else {
                const snackBarData: ErrorSnackBarData = {
                  type: 'ErrorMessage',
                  message: 'GNR_TASK_DELETE_FAILED',
                };
                this.snackBarService.showSnackBar(snackBarData);
                return of(
                  WorkflowActions.deleteWorkflowTaskConfigurationFailure({
                    error: new Error(sts?.message || 'GNR_TASK_DELETE_FAILED'),
                    mysid: subscriptionMysid,
                    refId: subscriptionRefId,
                  })
                );
              }

            default:
              return of({ type: 'NOT_EXIST' });
          }
        }
      ),
      catchError(error =>
        of(
          WorkflowActions.loadWorkflowTaskConfigurationsFailure({
            error,
            mysid: '',
            refId: '',
          })
        )
      )
    )
  );

  subscribeMyDealsWorkflows$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WorkflowActions.subscribeMyDealsWorkflows),
      switchMap(() =>
        this.workflowService.subscribeMyDealsWorkflows().pipe(
          takeUntil(
            this.actions$.pipe(
              ofType(WorkflowActions.unsubscribeMyDealsWorkflows)
            )
          ),
          filter((data: any) => data.op === 'query'),
          map(({ res, sts }: any) => {
            if (res || !isNil(sts?.meta)) {
              return WorkflowActions.loadMyDealsWorkflowsSuccess({
                workflows: res || [],
              });
            }
            return WorkflowActions.loadMyDealsWorkflowsFailure({
              error: new Error(
                sts?.message || 'GNR_FAILED_TO_SUBSCRIBE_WORKFLOWS'
              ),
            });
          }),
          catchError(error =>
            of(WorkflowActions.loadMyDealsWorkflowsFailure({ error }))
          )
        )
      )
    )
  );

  copyWorkflows$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WorkflowActions.copyWorkflows),
      mergeMap(({ sourceWorkflowIds, targetDealRefId }) =>
        this.workflowService
          .batchCopyWorkflows(sourceWorkflowIds, targetDealRefId)
          .pipe(
            map(response => {
              if (response?.res) {
                const snackBarData: NormalSnackBarData = {
                  type: 'NormalMessage',
                  message: 'Workflows copied successfully',
                };
                this.snackBarService.showSnackBar(snackBarData);
                return WorkflowActions.copyWorkflowsSuccess({
                  copiedWorkflows: response.res.copiedWorkflows ?? [],
                  targetDealRefId,
                });
              }
              const errorSnack: ErrorSnackBarData = {
                type: 'ErrorMessage',
                message: 'Failed to copy workflows',
              };
              this.snackBarService.showSnackBar(errorSnack);
              return WorkflowActions.copyWorkflowsFailure({
                error: new Error('Failed to copy workflows'),
                targetDealRefId,
              });
            }),
            catchError(error => {
              const beDetail =
                error?.error?.sts?.message ||
                error?.error?.message ||
                error?.error?.error ||
                (typeof error?.error === 'string' ? error.error : null);
              const errorSnack: ErrorSnackBarData = {
                type: 'ErrorMessage',
                message: beDetail
                  ? `Failed to copy workflows: ${beDetail}`
                  : 'Failed to copy workflows',
              };
              this.snackBarService.showSnackBar(errorSnack);
              return of(
                WorkflowActions.copyWorkflowsFailure({
                  error,
                  targetDealRefId,
                })
              );
            })
          )
      )
    )
  );

  unsubscribeWorkflowHistory$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(WorkflowActions.unsubscribeWorkflowHistory),
        map(({ workflowId, mysid }) => {
          console.log(
            '[Workflow Effects] Unsubscribed from workflow history:',
            workflowId,
            mysid
          );
        })
      ),
    { dispatch: false }
  );

  subscribeWorkflowHistory$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WorkflowActions.subscribeWorkflowHistory),
      mergeMap(action => {
        const { payload, workflowId, mysid } = action;

        const messages$ = this.workflowStateService.subscribe(payload).pipe(
          map((message: any) => ({
            ...message,
            subscriptionWorkflowId: workflowId,
          })),
          filter((data: any) => data.op),
          takeUntil(
            this.actions$.pipe(
              ofType(WorkflowActions.unsubscribeWorkflowHistory),
              filter(a => a.workflowId === workflowId && a.mysid === mysid)
            )
          ),
          share()
        );

        const runs$ = messages$.pipe(
          filter((data: any) => data.view === 'runs'),
          mergeMap(({ op, res, sts, subscriptionWorkflowId }: any) => {
            switch (op) {
              case 'query':
                if (res || !isNil(sts?.meta)) {
                  return of(
                    WorkflowActions.loadWorkflowRunsSuccess({
                      runs: res || [],
                      workflowId: subscriptionWorkflowId,
                    })
                  );
                } else {
                  return of(
                    WorkflowActions.loadWorkflowRunsFailure({
                      error: new Error(
                        sts?.message || 'GNR_FAILED_TO_LOAD_WORKFLOW_RUNS'
                      ),
                      workflowId: subscriptionWorkflowId,
                    })
                  );
                }

              case 'ins':
              case 'upd':
                if (!res) {
                  return of({ type: 'NOT_EXIST' });
                }
                return of(
                  WorkflowActions.upsertWorkflowRun({
                    workflowId: subscriptionWorkflowId,
                    run: res,
                  })
                );

              default:
                return of({ type: 'NOT_EXIST' });
            }
          }),
          catchError(error =>
            of(
              WorkflowActions.loadWorkflowRunsFailure({
                error,
                workflowId: '',
              })
            )
          )
        );

        const history$ = messages$.pipe(
          filter((data: any) => data.view === 'history'),
          mergeMap(({ res, sts, subscriptionWorkflowId, id }: any) => {
            const runId = res?.workflowStateId ?? id ?? '';
            if (res) {
              return of(
                WorkflowActions.loadWorkflowHistorySuccess({
                  history: res,
                  workflowId: subscriptionWorkflowId,
                  runId,
                })
              );
            } else {
              return of(
                WorkflowActions.loadWorkflowHistoryFailure({
                  error: new Error(
                    sts?.message || 'GNR_FAILED_TO_LOAD_WORKFLOW_HISTORY'
                  ),
                  workflowId: subscriptionWorkflowId,
                  runId,
                })
              );
            }
          }),
          catchError(error =>
            of(
              WorkflowActions.loadWorkflowHistoryFailure({
                error,
                workflowId: '',
                runId: '',
              })
            )
          )
        );

        return merge(runs$, history$);
      })
    )
  );

  getWorkflowHistoryByRun$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WorkflowActions.getWorkflowHistoryByRun),
      switchMap(({ runId, workflowId, mysid }) => {
        this.workflowStateService.getHistoryByRun(runId, mysid);
        return this.workflowStateService.getHistoryByRunResponse(mysid).pipe(
          map((response: any) => {
            if (response.res) {
              return WorkflowActions.loadWorkflowHistorySuccess({
                history: response.res,
                workflowId,
                runId,
              });
            }
            return WorkflowActions.loadWorkflowHistoryFailure({
              error: new Error(
                response.sts?.message || 'GNR_FAILED_TO_LOAD_WORKFLOW_HISTORY'
              ),
              workflowId,
              runId,
            });
          }),
          catchError(error =>
            of(
              WorkflowActions.loadWorkflowHistoryFailure({
                error,
                workflowId,
                runId,
              })
            )
          )
        );
      })
    )
  );
}
